import React, { memo } from 'react';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/button';
import Select from '../../../components/ui/select';
import StatusIndicator from '../../../components/ui/statusIndicator';
import EmployeeNavigation from 'layouts/employeeNavigation';
import useAttendanceSummaryLogic from 'hooks/useSummary';

// Memoized StatCard component to prevent unnecessary re-renders
const StatCard = memo(({ icon, iconColor, label, value, valueColor = "text-foreground" }) => (
    <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-2">
            <Icon name={icon} size={16} className={iconColor} />
            <span className="text-sm text-muted-foreground">{label}</span>
        </div>
        <p className={`text-2xl font-bold ${valueColor}`}>{value}</p>
    </div>
));

// Memoized MetricCard component to prevent unnecessary re-renders
const MetricCard = memo(({ label, value, icon, iconColor = "text-accent", valueColor = "text-foreground" }) => (
    <div className="bg-muted rounded-lg p-4">
        <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{label}</span>
            <Icon name={icon} size={16} className={iconColor} />
        </div>
        <p className={`text-xl font-semibold ${valueColor} mt-1`}>{value}</p>
    </div>
));

// Memoized AttendanceImage component to prevent unnecessary re-renders and image reloading
const AttendanceImage = memo(({ src, alt }) => {
    const [imgSrc, setImgSrc] = React.useState(src);
    const [hasError, setHasError] = React.useState(false);
    console.log("src",src)
    React.useEffect(() => {
        setImgSrc(src);
        setHasError(false);
    }, [src]);

    const handleError = () => {
        if (!hasError) {
            setHasError(true);
            setImgSrc('/images/placeholder-avatar.png');
        }
    };
    console.log("imgSrc",imgSrc)
    return (
        <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
            <img 
                src={imgSrc} 
                alt={alt} 
                className="w-full h-full object-cover"
                onError={handleError}
            />
        </div>
    );
});

// Memoized AttendanceRow component to prevent unnecessary re-renders
const AttendanceRow = memo(({ record, getStatusColor, getStatusLabel, formatTime }) => {
    // Get the pre-calculated image URL directly from the record
    console.log("imageUrl",record)
    const imageUrl = record.imageUrl || '/images/placeholder-avatar.png';
    
    return (
        <tr className="border-t border-border hover:bg-muted/50">
            {/* <td className="p-4">
                <AttendanceImage src={imageUrl} alt="Attendance record" />
            </td> */}
            <td className="p-4 text-sm text-foreground">
                {new Date(record.date)?.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                })}
            </td>
            <td className="p-4 text-sm font-mono text-foreground">
                {formatTime(record?.checkIn)}
            </td>
            <td className="p-4 text-sm font-mono text-foreground">
                {formatTime(record?.checkOut)}
            </td>
            <td className="p-4 text-sm font-mono text-foreground">
                {record?.totalHours || '--'}
            </td>
            <td className="p-4">
                <div className="flex items-center space-x-2">
                    <StatusIndicator type={getStatusColor(record?.status)} size="sm" />
                    <span className="text-sm text-foreground">{getStatusLabel(record?.status)}</span>
                </div>
            </td>
            <td className="p-4 text-sm text-muted-foreground">
                {record?.location || '--'}
            </td>
        </tr>
    );
});

const AttendanceSummaryTab = () => {
    // Use the custom hook to get all the state and functions
    const {
        isCollapsed,
        employee,
        selectedMonth,
        selectedFilter,
        loading,
        error,
        attendanceStats,
        attendanceHistory,
        monthOptions,
        filterOptions,
        filteredAttendance,
        setIsCollapsed,
        setSelectedMonth,
        setSelectedFilter,
        getStatusColor,
        getStatusLabel,
        formatTime,
        getRecordImageUrl,
        handleExportData,
        handleToggleCollapse,
        user
    } = useAttendanceSummaryLogic();

    return (
        <div className="min-h-screen bg-background">
            <EmployeeNavigation
                user={user}
                isCollapsed={isCollapsed}
                onToggleCollapse={handleToggleCollapse}
            />
            <main className={`pt-16 transition-all duration-200 ease-in-out ${isCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
                <div className='p-6 space-y-6'>
                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row gap-4">
                        <Select
                            label="Select Month"
                            options={monthOptions}
                            value={selectedMonth}
                            onChange={setSelectedMonth}
                            className="sm:w-48"
                        />
                        <Select
                            label="Filter Records"
                            options={filterOptions}
                            value={selectedFilter}
                            onChange={setSelectedFilter}
                            className="sm:w-48"
                        />
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                            <div className="flex items-center space-x-2">
                                <Icon name="AlertCircle" size={16} className="text-error" />
                                <span className="text-error">Error: {error}</span>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="mt-2"
                                onClick={() => window.location.reload()}
                            >
                                Try Again
                            </Button>
                        </div>
                    )}

                    {/* Data Display */}
                    {!loading && !error && (
                        <>
                            {/* Attendance Statistics */}
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-4">Monthly Summary</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <StatCard
                                        icon="Calendar"
                                        iconColor="text-muted-foreground"
                                        label="Working Days"
                                        value={attendanceStats?.totalWorkingDays || 0}
                                    />
                                    <StatCard
                                        icon="CheckCircle"
                                        iconColor="text-success"
                                        label="Present"
                                        value={attendanceStats?.presentDays || 0}
                                        valueColor="text-success"
                                    />
                                    <StatCard
                                        icon="XCircle"
                                        iconColor="text-error"
                                        label="Absent"
                                        value={attendanceStats?.absentDays || 0}
                                        valueColor="text-error"
                                    />
                                    <StatCard
                                        icon="Clock"
                                        iconColor="text-warning"
                                        label="Late Arrivals"
                                        value={attendanceStats?.lateDays || 0}
                                        valueColor="text-warning"
                                    />
                                </div>
                            </div>

                            {/* Additional Metrics */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <MetricCard
                                    label="Average Hours/Day"
                                    value={attendanceStats?.averageHours || '0h 0m'}
                                    icon="TrendingUp"
                                />
                                <MetricCard
                                    label="Total Hours"
                                    value={attendanceStats?.totalHours || '0h 0m'}
                                    icon="Clock"
                                />
                                <MetricCard
                                    label="Attendance Rate"
                                    value={`${attendanceStats?.attendancePercentage || 0}%`}
                                    icon="Target"
                                    iconColor="text-success"
                                    valueColor="text-success"
                                />
                            </div>

                            {/* Recent Attendance Records */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Recent Attendance Records</h3>
                                    <div className="flex items-center space-x-2">
                                        <Select
                                            label="Filter Records"
                                            options={filterOptions}
                                            value={selectedFilter}
                                            onChange={setSelectedFilter}
                                            className="sm:w-48"
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            iconName="Download"
                                            iconPosition="left"
                                            onClick={handleExportData}
                                        >
                                            Export Data
                                        </Button>
                                    </div>
                                </div>

                                <div className="bg-card border border-border rounded-lg overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-muted">
                                                <tr>
                                                    {/* <th className="text-left p-4 text-sm font-medium text-muted-foreground">Photo</th> */}
                                                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                                                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Check In</th>
                                                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Check Out</th>
                                                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Hours</th>
                                                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                                                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Location</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredAttendance?.length > 0 ? (
                                                    filteredAttendance?.map((record) => (
                                                        <AttendanceRow
                                                            key={record?.id}
                                                            record={record}
                                                            getStatusColor={getStatusColor}
                                                            getStatusLabel={getStatusLabel}
                                                            formatTime={formatTime}
                                                        />
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="p-4 text-center text-muted-foreground">
                                                            No attendance records found for the selected criteria.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default AttendanceSummaryTab;